import React, { useState } from 'react';
import { UserPlus, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { createInvite } from '../../lib/meApi';

const APP_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * "Voy con..." — abre un modal donde se genera un enlace personalizado
 * para invitar amigos al evento. Aceptar la invitación suma puntos al inviter.
 */
const InviteFriendButton = ({
  eventId,
  eventName,
  isAuthenticated = false,
  onLoginRequired,
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleClick = async () => {
    if (!isAuthenticated) {
      onLoginRequired?.();
      toast.info('Inicia sesión para invitar amigos.');
      return;
    }
    setLoading(true);
    try {
      const res = await createInvite(eventId);
      setLink(`${APP_ORIGIN}${res.url_path}`);
      setOpen(true);
    } catch {
      toast.error('No se pudo crear la invitación');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      toast.success('Enlace copiado 🎉');
      setTimeout(() => setCopied(false), 2200);
    });
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(`¡Vamos a ${eventName}! Te invito vía PlanCity 🎉 ${link}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const size = compact ? 'w-8 h-8' : 'w-10 h-10';
  const iconSize = compact ? 'w-4 h-4' : 'w-5 h-5';

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        title="Invita a un amigo"
        aria-label="Invita a un amigo"
        data-testid={`engagement-invite-${eventId}`}
        className={`${size} rounded-full backdrop-blur-md bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95 disabled:opacity-60`}
      >
        <UserPlus className={`${iconSize} text-gray-700`} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invita a un amigo 🎉</DialogTitle>
            <DialogDescription>
              Comparte este enlace. Cuando tu amigo lo acepte ganarás <strong className="text-emerald-600">+50 PlanPoints</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Input
                value={link}
                readOnly
                className="rounded-xl text-xs"
                data-testid="invite-link-input"
                onFocus={(e) => e.target.select()}
              />
              <Button onClick={handleCopy} className="rounded-xl shrink-0" data-testid="invite-copy-btn">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <Button
              onClick={handleWhatsApp}
              className="w-full rounded-full bg-[#25D366] hover:bg-[#1FB956] text-white"
              data-testid="invite-whatsapp-btn"
            >
              Compartir por WhatsApp
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InviteFriendButton;
